import React, { useState, useEffect, MouseEvent } from 'react';
import TopNav from './TopNav/TopNav';
import SideNav from './SideNav/SideNav';
import { IS_SIDE_NAVIGATION_OPEN, PAGE_INFO } from 'src/util/constants';
import { FmtpPage } from './types';
import { Permissions } from '@lanterds/types';
import { userManagementEnabled, shippingPortalEnabled, navigationEnabled, reportsEnabled } from 'src/util/featureFlags';
import { useKeycloak } from '@react-keycloak/web';
import { isAuthorized } from 'src/util/Permissions';
import { PageNames } from 'src/util/types';
import { SideNavContextProvider } from 'src/components/common/NavigationMenu/SideNav/SideNavContext';
import { useReportsContext } from '../../Views/Reports/ReportsContext';

export interface NavigationMenuProps {
    selectedNavItem?: string;
    drawerWidth?: number;
}

export default function NavigationMenu({ selectedNavItem, drawerWidth }: NavigationMenuProps) {
    const isSessionDrawerOpen = sessionStorage.getItem(IS_SIDE_NAVIGATION_OPEN) ?? true;
    const [isDrawerOpen, setIsDrawerOpen] = useState(isSessionDrawerOpen === 'true');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { reports } = useReportsContext();
    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        sessionStorage.clear();
        keycloak.logout();
    };

    const { keycloak } = useKeycloak();
    const { tokenParsed } = keycloak;
    const userDisplayName =
        tokenParsed?.given_name && tokenParsed?.family_name
            ? `${tokenParsed.given_name.charAt(0)}. ${tokenParsed.family_name}`
            : tokenParsed?.name;
    const userEmail = tokenParsed?.email;

    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    useEffect(() => {
        sessionStorage.setItem(IS_SIDE_NAVIGATION_OPEN, String(isDrawerOpen));
    }, [isDrawerOpen]);

    // ALSO ADD NEW PERMISSIONS TO THEIR RELATIVE LAYOUT FILES
    const isParcelSearchAuthorized = isAuthorized(userEmail, Permissions.parcelSearch);
    const isShippingPortalReturnsAuthorized = isAuthorized(userEmail, Permissions.shippingPortal_returns);
    const isShippingPortalTransfersAuthorized = isAuthorized(userEmail, Permissions.shippingPortal_transfers);
    const isShippingPortalOutboundAuthorized = isAuthorized(userEmail, Permissions.shippingPortal_outbound);
    const isUserManagementAuthorized = isAuthorized(userEmail, Permissions.userManagement);
    const isReportsAuthorized = [
        Permissions.reports_transferReport,
        Permissions.reports_returnsReport,
        Permissions.reports_otdReport,
        Permissions.reports_eltMetricsReport,
        Permissions.reports_plsReportAllSources,
        Permissions.reports_volumeReportAllSources,
        Permissions.reports_billing,
        Permissions.reports_tenderExceptionReport,
        Permissions.reports_distressedParcels,
        Permissions.reports_fmtpActivityReport,
        Permissions.reports_assetTracking,
        Permissions.reports_scanDetail,
        Permissions.reports_osdmReport,
        Permissions.reports_transfers,
        Permissions.reports_returns,
        Permissions.reports_osd_manifest,
    ].some((permission) => {
        return isAuthorized(userEmail, permission);
    });
    const isShipmentTrackerAuthorized = isAuthorized(userEmail, Permissions.shipmentTracking);
    const isDeliveryManagementAuthorized = isAuthorized(userEmail, Permissions.deliveryManagementTool);

    const appNavigationLinks: FmtpPage[] = [];
    const authorizedLabelsSubPages: PageNames[] = [];

    // This sets the order of the navigation menu
    if (isParcelSearchAuthorized) appNavigationLinks.push(PAGE_INFO.parcelSearch);
    if (isShipmentTrackerAuthorized) appNavigationLinks.push(PAGE_INFO.shipmentTracker);
    if (
        shippingPortalEnabled &&
        (isShippingPortalReturnsAuthorized || isShippingPortalTransfersAuthorized || isShippingPortalOutboundAuthorized)
    )
        appNavigationLinks.push(PAGE_INFO.shippingPortal);

    if (shippingPortalEnabled) {
        if (isShippingPortalReturnsAuthorized) {
            authorizedLabelsSubPages.push(PageNames.Return);
        }
        if (isShippingPortalTransfersAuthorized) {
            authorizedLabelsSubPages.push(PageNames.Transfer);
        }
        if (isShippingPortalOutboundAuthorized) {
            authorizedLabelsSubPages.push(PageNames.Outbound);
        }
    }

    if (isDeliveryManagementAuthorized) appNavigationLinks.push(PAGE_INFO.deliveryManagement);
    if (reportsEnabled && isReportsAuthorized) appNavigationLinks.push(PAGE_INFO.reports);
    if (userManagementEnabled && isUserManagementAuthorized) appNavigationLinks.push(PAGE_INFO.userManagement);

    return (
        <>
            <TopNav
                selectedNavItem={selectedNavItem}
                navLinks={
                    navigationEnabled ? appNavigationLinks : appNavigationLinks?.filter((link) => link.name === selectedNavItem)
                }
                isDrawerOpen={isDrawerOpen}
                toggleDrawer={toggleDrawer}
                userMenuClickHandler={handleClick}
                userDisplayName={userDisplayName}
                anchorElement={anchorEl}
                menuCloseHandler={handleClose}
                logoutHandler={handleLogout}
                authorizedLabelsSubPages={authorizedLabelsSubPages}
                // Keeping reports sub pages separate because we dynamically fetch them and do not know the page names
                reportsSubPages={reports}
            />
            <SideNavContextProvider>
                <SideNav
                    navLinks={
                        navigationEnabled
                            ? appNavigationLinks
                            : appNavigationLinks?.filter((link) => link.name === selectedNavItem)
                    }
                    toggleDrawer={toggleDrawer}
                    isDrawerOpen={isDrawerOpen}
                    selectedNavItem={selectedNavItem}
                    drawerWidth={drawerWidth}
                    userDisplayName={userDisplayName}
                    logoutHandler={handleLogout}
                />
            </SideNavContextProvider>
        </>
    );
}
